import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { newHttpService } from '../services/new-http-service'
import { useCachedFetch } from '../hooks/use-cached-fetch'
import { Spinner } from '../cmps/util-cmps/spinner'
import { getAuditControlsByCompanyNameQuery } from '../cmps/util-cmps/graphql-queries/audit-controls-query'

type ViewMode = 'companies' | 'systems'

interface CompanySummary {
  id: string
  name: string
  abbreviation?: string | null
  logoUrl?: string | null
  isSilo?: boolean
  activeControls: any[]
  activeControlCount: number
  systemNames: string[]
  systemCount: number
  entityNames: string[]
  entityCount: number
}

interface SystemSummary {
  id: string
  key: string
  name: string
  abbreviation?: string | null
  logoUrl?: string | null
  activeControls: any[]
  activeControlCount: number
  companyNames: string[]
  companyCount: number
}

const normalizeKey = (value: string) => value.toLowerCase().replace(/[\s_-]+/g, '')

const getControlSystems = (control: any) => [
  control?.mainSystem?.system,
  control?.hrSystem?.system,
  control?.ticketSystem?.system,
].filter(Boolean)

const getSystemKey = (system: any) => {
  const name = system?.name || system?.abbreviation || ''
  return normalizeKey(name)
}

const getLogoAlt = (label: string, kind: 'company' | 'system') => `${kind === 'company' ? 'Company' : 'System'} logo for ${label}`

const LogoMark: React.FC<{
  label: string
  logoUrl?: string | null
  kind: 'company' | 'system'
  className?: string
}> = ({ label, logoUrl, kind, className }) => {
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    setHasError(false)
  }, [logoUrl])

  if (logoUrl && !hasError) {
    return (
      <img
        className={className}
        src={logoUrl}
        alt={getLogoAlt(label, kind)}
        onError={() => setHasError(true)}
      />
    )
  }

  const fallback = label.trim().slice(0, 2).toUpperCase() || (kind === 'company' ? 'CO' : 'SY')
  return (
    <span className={className} aria-hidden="true">
      {fallback}
    </span>
  )
}

const ManagerDashboardPage: React.FC = () => {
  const navigate = useNavigate()
  const { data: companiesData, isLoading: companiesLoading, hasError: companiesError } = useCachedFetch(
    'companies-list',
    async () => {
      const result = await newHttpService.graphql('auditech', `
        {
          companies {
            id
            name
            abbreviation
            logoUrl
            isSilo
            entity(sorting: ASC) {
              id
              name
              abbreviation
            }
          }
        }
      `)
      const companies = result.companies || []
      const siloCompanies = companies.filter((company: any) => company?.isSilo)
      if (siloCompanies.length === 0) {
        return companies
      }

      const siloCompanyResults = await Promise.all(
        siloCompanies.map(async (company: any) => {
          try {
            const siloResult = await newHttpService.graphql(company.name, `
              {
                companies(name: "${company.name}") {
                  id
                  name
                  abbreviation
                  logoUrl
                  isSilo
                  entity(sorting: ASC) {
                    id
                    name
                    abbreviation
                  }
                }
              }
            `)
            return { name: company.name, company: siloResult.companies?.[0] ?? company }
          } catch {
            throw new Error(`Failed to load silo data for ${company.name}`)
          }
        })
      )

      const siloCompanyMap = new Map<string, any>()
      for (const entry of siloCompanyResults) {
        siloCompanyMap.set(entry.name, entry.company)
      }

      return companies.map((company: any) => siloCompanyMap.get(company.name) ?? company)
    }
  )
  const companies = useMemo(() => {
    const byName = new Map<string, any>()
    for (const company of companiesData || []) {
      if (!company?.name) continue
      const existing = byName.get(company.name)
      if (!existing || (!existing.isSilo && company.isSilo)) {
        byName.set(company.name, company)
      }
    }
    return Array.from(byName.values())
  }, [companiesData])
  const companyNames = useMemo(() => companies.map((company: any) => company.name).filter(Boolean), [companies])
  const [viewMode, setViewMode] = useState<ViewMode>('companies')

  const { data: systems, isLoading: systemsLoading, hasError: systemsError } = useCachedFetch(
    'manager-dashboard:systems',
    async () => {
      const result = await newHttpService.graphql('auditech', `
        {
          systems {
            id
            name
            abbreviation
            logoUrl
            type
          }
        }
      `)
      return result.systems || []
    }
  )

  const { data: companyControls, isLoading: controlsLoading, hasError: controlsError } = useCachedFetch(
    companyNames.length > 0 ? `manager-dashboard:controls:${companyNames.join('|')}` : null,
    async () => {
      const results = await Promise.all(companyNames.map(async (companyName: string) => {
        try {
          const result = await newHttpService.graphql(companyName, getAuditControlsByCompanyNameQuery(companyName))
          return { companyName, controls: result.auditControlsByCompanyName || [] }
        } catch {
          return { companyName, controls: [] }
        }
      }))
      return results
    }
  )

  const activeCompanySummaries = useMemo<CompanySummary[]>(() => {
    const controlsByCompany = new Map<string, any[]>()
    for (const item of companyControls || []) {
      const controls = (item.controls || []).filter((control: any) => control.isAutomated && !control.isHidden)
      controlsByCompany.set(item.companyName, controls)
    }

    return companies
      .map((company: any) => {
        const activeControls = controlsByCompany.get(company.name) || []
        const systemNames = new Set<string>()
        const entityNames = Array.from(
          new Set<string>(
            (company.entity || [])
              .map((entity: any) => entity?.name || entity?.abbreviation)
              .filter((value: unknown): value is string => Boolean(value))
          )
        ).sort((left, right) => left.localeCompare(right))
        for (const control of activeControls) {
          for (const system of getControlSystems(control.control)) {
            const key = system?.name || system?.abbreviation
            if (key) systemNames.add(key)
          }
        }
        return {
          id: String(company.id),
          name: company.name,
          abbreviation: company.abbreviation,
          logoUrl: company.logoUrl,
          isSilo: company.isSilo,
          activeControls,
          activeControlCount: activeControls.length,
          systemNames: Array.from(systemNames).sort((left, right) => left.localeCompare(right)),
          systemCount: systemNames.size,
          entityNames,
          entityCount: (company.entity || []).length,
        }
      })
      .filter((item: CompanySummary) => item.activeControlCount > 0)
      .sort((left: CompanySummary, right: CompanySummary) => (
        right.activeControlCount - left.activeControlCount || left.name.localeCompare(right.name)
      ))
  }, [companies, companyControls])

  const activeSystemSummaries = useMemo<SystemSummary[]>(() => {
    const systemsByKey = new Map<string, any>()
    for (const system of systems || []) {
      const key = getSystemKey(system)
      if (!key) continue
      systemsByKey.set(key, system)
    }

    const summaryMap = new Map<string, SystemSummary>()
    for (const item of companyControls || []) {
      const company = companies.find((entry: any) => entry.name === item.companyName)
      const activeControls = (item.controls || []).filter((control: any) => control.isAutomated && !control.isHidden)
      for (const control of activeControls) {
        const seenKeys = new Set<string>()
        for (const system of getControlSystems(control.control)) {
          const key = getSystemKey(system)
          if (!key || seenKeys.has(key)) continue
          seenKeys.add(key)
          const catalogSystem = systemsByKey.get(key) || system
          const existing = summaryMap.get(key)
          if (existing) {
            existing.activeControls.push({
              ...control,
              companyName: item.companyName,
            })
            if (!existing.companyNames.includes(item.companyName)) existing.companyNames.push(item.companyName)
            continue
          }
          summaryMap.set(key, {
            id: String(catalogSystem?.id ?? key),
            key,
            name: catalogSystem?.name || system?.name || system?.abbreviation || key,
            abbreviation: catalogSystem?.abbreviation || system?.abbreviation || null,
            logoUrl: catalogSystem?.logoUrl || system?.logoUrl || null,
            activeControls: [{
              ...control,
              companyName: item.companyName,
            }],
            activeControlCount: 1,
            companyNames: company ? [item.companyName] : [item.companyName],
            companyCount: 1,
          })
        }
      }
    }

    return Array.from(summaryMap.values())
      .map(system => ({
        ...system,
        activeControlCount: system.activeControls.length,
        companyCount: new Set(system.companyNames).size,
      }))
      .sort((left, right) => right.activeControlCount - left.activeControlCount || left.name.localeCompare(right.name))
  }, [companies, companyControls, systems])

  const visibleSummaries = viewMode === 'companies' ? activeCompanySummaries : activeSystemSummaries
  const [selectionKey, setSelectionKey] = useState<string | null>(null)

  useEffect(() => {
    if (visibleSummaries.length === 0) {
      setSelectionKey(null)
      return
    }

    const currentSelectionExists = visibleSummaries.some((item: any) => (
      viewMode === 'companies'
        ? item.name === selectionKey
        : item.key === selectionKey
    ))

    if (!currentSelectionExists) {
      setSelectionKey(viewMode === 'companies'
        ? (visibleSummaries[0] as CompanySummary).name
        : (visibleSummaries[0] as SystemSummary).key)
    }
  }, [selectionKey, visibleSummaries, viewMode])

  const totalActiveControls = activeCompanySummaries.reduce((sum, item) => sum + item.activeControlCount, 0)
  const totalActiveSystems = activeSystemSummaries.length
  const companyCount = activeCompanySummaries.length
  const entityCount = activeCompanySummaries.reduce((sum, item) => sum + item.entityCount, 0)
  const entitySummaryText = `${entityCount} entities under ${companyCount} companies`

  const summaryCards = [
    { label: 'Companies with active controls', value: companyCount },
    { label: 'Active audit controls', value: totalActiveControls },
    { label: 'Systems in use', value: totalActiveSystems },
  ]

  const isLoading = companiesLoading || controlsLoading || systemsLoading
  const hasError = companiesError || controlsError || systemsError

  return (
    <div className="tablePageContainer manager-dashboard-page">
      <div className="page-header manager-dashboard-header">
        <div>
          <p className="tableSubtitle">High-level overview of the companies and systems that currently carry active audit controls.</p>
        </div>
        <button type="button" className="add-btn" onClick={() => navigate('/companies/audit-controls')}>
          Open Audit Controls
        </button>
      </div>

      <div className="manager-dashboard-scroll">
        <section className="manager-dashboard-summary" aria-label="Dashboard metrics">
          {summaryCards.map(card => (
            <article key={card.label} className="manager-dashboard-kpi">
              <span className="manager-dashboard-kpi__label">{card.label}</span>
              <span className="manager-dashboard-kpi__value">{card.value}</span>
            </article>
          ))}
        </section>

        {isLoading && (
          <div className="manager-dashboard-loading">
            <Spinner />
            <span>Loading companies, entities, systems, and audit-control usage...</span>
          </div>
        )}

        {!isLoading && hasError && (
          <div className="error-hint">Failed to load dashboard data. Try refreshing the page or re-logging in.</div>
        )}

        {!isLoading && !hasError && (
          <section className="manager-dashboard-shell">
            <div className="manager-dashboard-toolbar">
              <div className="manager-dashboard-segmented" role="tablist" aria-label="Dashboard view">
                <button
                  type="button"
                  className={`manager-dashboard-segmented__button${viewMode === 'companies' ? ' is-active' : ''}`}
                  onClick={() => setViewMode('companies')}
                  aria-pressed={viewMode === 'companies'}
                >
                  Companies
                  <span>{activeCompanySummaries.length}</span>
                </button>
                <button
                  type="button"
                  className={`manager-dashboard-segmented__button${viewMode === 'systems' ? ' is-active' : ''}`}
                  onClick={() => setViewMode('systems')}
                  aria-pressed={viewMode === 'systems'}
                >
                  Systems
                  <span>{activeSystemSummaries.length}</span>
                </button>
              </div>
            </div>

            <section className="manager-dashboard-panel">
              <div className="manager-dashboard-panel__header">
                <div>
                  <p className="manager-dashboard-panel__eyebrow">{viewMode === 'companies' ? 'Companies' : 'Systems'}</p>
                  <h2>{viewMode === 'companies' ? 'Company footprint' : 'Systems in use by active controls'}</h2>
                  {viewMode === 'companies' && (
                    <p className="manager-dashboard-panel__subtext">{entitySummaryText}</p>
                  )}
                </div>
              </div>

              <div className="manager-dashboard-grid">
                {visibleSummaries.map((item: any) => {
                  const isSelected = viewMode === 'companies'
                    ? selectionKey === item.name
                    : selectionKey === item.key

                  return (
                    <button
                      key={viewMode === 'companies' ? item.id : item.key}
                      type="button"
                      className={`manager-dashboard-card${isSelected ? ' is-selected' : ''}`}
                      onClick={() => setSelectionKey(viewMode === 'companies' ? item.name : item.key)}
                    >
                      <div className="manager-dashboard-card__logo">
                        <LogoMark
                          label={item.name}
                          logoUrl={item.logoUrl}
                          kind={viewMode === 'companies' ? 'company' : 'system'}
                          className="manager-dashboard-card__logo-image"
                        />
                      </div>
                      <div className="manager-dashboard-card__body">
                        <div className="manager-dashboard-card__title-row">
                          <h3>{item.name}</h3>
                          {viewMode === 'companies' && item.isSilo && <span className="manager-dashboard-badge">Silo</span>}
                          {viewMode === 'systems' && item.abbreviation && <span className="manager-dashboard-badge">{item.abbreviation}</span>}
                        </div>
                        <div className="manager-dashboard-card__meta">
                          <span>{item.activeControlCount} active controls</span>
                          {viewMode === 'companies' ? (
                            <>
                              <span>{item.systemCount} systems</span>
                              <span>{item.entityCount} entities</span>
                            </>
                          ) : (
                            <span>{item.companyCount} companies</span>
                          )}
                        </div>
                        {viewMode === 'companies' ? (
                          <>
                            <div className="manager-dashboard-chip-group">
                              <span className="manager-dashboard-chip-group__label">Systems</span>
                              <div className="manager-dashboard-chip-row" aria-label={`${item.name} related systems`}>
                                {item.systemNames.slice(0, 4).map((label: string) => (
                                  <span key={label} className="manager-dashboard-chip">{label}</span>
                                ))}
                                {item.systemNames.length > 4 && (
                                  <span className="manager-dashboard-chip manager-dashboard-chip--muted">
                                    +{item.systemNames.length - 4}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="manager-dashboard-chip-group">
                              <span className="manager-dashboard-chip-group__label">Entities</span>
                              <div className="manager-dashboard-chip-row" aria-label={`${item.name} related entities`}>
                                {item.entityNames.slice(0, 4).map((label: string) => (
                                  <span key={label} className="manager-dashboard-chip manager-dashboard-chip--entity">{label}</span>
                                ))}
                                {item.entityNames.length > 4 && (
                                  <span className="manager-dashboard-chip manager-dashboard-chip--muted">
                                    +{item.entityNames.length - 4}
                                  </span>
                                )}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="manager-dashboard-chip-row" aria-label={`${item.name} related companies`}>
                            {item.companyNames.slice(0, 4).map((label: string) => (
                              <span key={label} className="manager-dashboard-chip">{label}</span>
                            ))}
                            {item.companyNames.length > 4 && (
                              <span className="manager-dashboard-chip manager-dashboard-chip--muted">
                                +{item.companyNames.length - 4}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </section>
          </section>
        )}

        {!isLoading && !hasError && visibleSummaries.length === 0 && (
          <div className="manager-dashboard-empty">
            No active audit controls were found for the loaded companies.
          </div>
        )}
      </div>
    </div>
  )
}

export default ManagerDashboardPage
